import {
  LuPackageCheck,
  LuHandshake,
  LuTruck,
  LuShieldAlert,
  LuBell,
  LuSearchCheck,
  LuClipboardCheck,
  LuArrowUpToLine,
  LuGitFork,
  LuRefreshCw,
  LuCalendarClock,
  LuPackageX,
  LuPackagePlus,
  LuSend,
  LuLink,
  LuArrowUpRight,
} from 'react-icons/lu';

import { ActionType, type CatalogItem, TriggerType, WorkflowNodeType } from './types';

export const NODE_WIDTH_PX = 280;
export const ROUTING_PATH_HEIGHT_PX = 48;
export const NODE_GAP_X = 200;

export const EDGE_STYLE = { stroke: 'var(--chakra-colors-gray-300)', strokeWidth: 1.5 };

export const TRIGGER_CATALOG: CatalogItem[] = [
  {
    id: 'trigger_demand_created',
    nodeType: WorkflowNodeType.Trigger,
    triggerType: TriggerType.DemandCreated,
    label: 'Demand Created',
    description: 'Triggers when a new business demand or need is raised',
    icon: LuPackageCheck,
  },
  {
    id: 'trigger_bid_received',
    nodeType: WorkflowNodeType.Trigger,
    triggerType: TriggerType.BidReceived,
    label: 'Bid Received',
    description: 'Triggers when a supplier bid or proposal is received',
    icon: LuHandshake,
  },
  {
    id: 'trigger_order_fulfilled',
    nodeType: WorkflowNodeType.Trigger,
    triggerType: TriggerType.OrderFulfilled,
    label: 'Order Fulfilled',
    description: 'Triggers when a supply order is delivered or fulfilled',
    icon: LuTruck,
  },
  {
    id: 'trigger_risk_alert_raised',
    nodeType: WorkflowNodeType.Trigger,
    triggerType: TriggerType.RiskAlertRaised,
    label: 'Risk Alert Raised',
    description: 'Triggers when a supply chain risk alert is detected',
    icon: LuShieldAlert,
  },
  {
    id: 'trigger_commitment_status_changed',
    nodeType: WorkflowNodeType.Trigger,
    triggerType: TriggerType.CommitmentStatusChanged,
    label: 'Commitment Status Changed',
    description: 'Triggers when a supply commitment changes status',
    icon: LuRefreshCw,
  },
  {
    id: 'trigger_delivery_deadline_approaching',
    nodeType: WorkflowNodeType.Trigger,
    triggerType: TriggerType.DeliveryDeadlineApproaching,
    label: 'Delivery Deadline Approaching',
    description: 'Triggers N days before a commitment delivery date',
    icon: LuCalendarClock,
  },
  {
    id: 'trigger_commitment_withdrawn',
    nodeType: WorkflowNodeType.Trigger,
    triggerType: TriggerType.CommitmentWithdrawn,
    label: 'Commitment Withdrawn',
    description: 'Triggers when a supply commitment is withdrawn',
    icon: LuPackageX,
  },
];

export const ACTION_CATALOG: CatalogItem[] = [
  {
    id: 'action_send_notification',
    nodeType: WorkflowNodeType.Action,
    actionType: ActionType.SendNotification,
    label: 'Send Notification',
    description: 'Notify stakeholders about a supply chain event',
    icon: LuBell,
  },
  {
    id: 'action_evaluate_risk',
    nodeType: WorkflowNodeType.Action,
    actionType: ActionType.EvaluateRisk,
    label: 'Evaluate Risk',
    description: 'Run a risk assessment on the triggering event',
    icon: LuSearchCheck,
  },
  {
    id: 'action_update_compliance',
    nodeType: WorkflowNodeType.Action,
    actionType: ActionType.UpdateCompliance,
    label: 'Update Compliance',
    description: 'Update compliance records and status tracking',
    icon: LuClipboardCheck,
  },
  {
    id: 'action_escalate_to_review',
    nodeType: WorkflowNodeType.Action,
    actionType: ActionType.EscalateToReview,
    label: 'Escalate to Review',
    description: 'Escalate to a review board for decision',
    icon: LuArrowUpToLine,
  },
  {
    id: 'action_create_need',
    nodeType: WorkflowNodeType.Action,
    actionType: ActionType.CreateNeed,
    label: 'Create Need',
    description: 'Auto-create a need to cover a supply gap',
    icon: LuPackagePlus,
  },
  {
    id: 'action_notify_partner',
    nodeType: WorkflowNodeType.Action,
    actionType: ActionType.NotifyPartner,
    label: 'Notify Partner',
    description: 'Send a notification to the counterparty',
    icon: LuSend,
  },
];

export const ROUTING_CATALOG: CatalogItem[] = [
  {
    id: 'routing_conditional',
    nodeType: WorkflowNodeType.Routing,
    label: 'Routing',
    description: 'Classify and route to different paths',
    icon: LuGitFork,
  },
];

// Finance / payment-routing nodes — shown in the picker for payment workflows.
export const FINANCE_CATALOG: CatalogItem[] = [
  {
    id: 'finance_payment_link',
    nodeType: WorkflowNodeType.PaymentLink,
    label: 'Payment Link',
    description: 'A link whose collections feed the staging pool',
    icon: LuLink,
  },
  {
    id: 'finance_payment_routing',
    nodeType: WorkflowNodeType.Routing,
    label: 'Payment Routing',
    description: 'Stage collections and disburse to ranked suppliers',
    icon: LuGitFork,
  },
  {
    id: 'finance_pay_supplier',
    nodeType: WorkflowNodeType.SupplierPayout,
    label: 'Pay Supplier',
    description: 'Send USDC to a supplier wallet',
    icon: LuArrowUpRight,
  },
];

export const ALL_CATALOG_ITEMS: CatalogItem[] = [
  ...TRIGGER_CATALOG,
  ...ACTION_CATALOG,
  ...ROUTING_CATALOG,
  ...FINANCE_CATALOG,
];
